import { useCallback } from "react"
import { useMutation } from "urql"

import type { SubmitPriceChangesInput, SubmitPriceChangesMutation } from "@/graphql/codegen/graphql"
import { SubmitPriceChangesDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseSubmitPriceChangesReturn {
  submitPriceChanges: (input: SubmitPriceChangesInput) => Promise<boolean>
  isSubmitting: boolean
}

export const useSubmitPriceChanges = (): UseSubmitPriceChangesReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<SubmitPriceChangesMutation, { input: SubmitPriceChangesInput }>(
    SubmitPriceChangesDocument
  )

  const submitPriceChanges = useCallback(
    async (input: SubmitPriceChangesInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      if (error || !data?.priceChange.submitPriceChanges) {
        notify({
          type: NotificationType.Error,
          message: error?.message ?? "Failed to submit price changes. Please try again.",
        })
        return false
      }
      notify({ type: NotificationType.Success, message: "Price changes submitted" })
      return true
    },
    [executeMutation, notify]
  )

  return { submitPriceChanges, isSubmitting: fetching }
}
